import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CurrencyDollar, Users, ImageSquare, UploadSimple, X, MusicNotes, Image as ImageIcon, Bank, CheckCircle, Timer, LockKey, ChartLineUp } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { fileUrl } from "@/lib/api";
import Countdown from "@/components/Countdown";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATS = [
  { slug: "feet", label: "Feet" }, { slug: "lingerie", label: "Lingerie" },
  { slug: "audio", label: "Fantasy Audio" }, { slug: "cosplay", label: "Cosplay" },
  { slug: "leather", label: "Leather & Latex" }, { slug: "voyeur", label: "Voyeur" },
  { slug: "general", label: "General" },
];

export default function Studio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dash, setDash] = useState(null);
  const [posts, setPosts] = useState([]);
  const [connect, setConnect] = useState(null);
  const [promos, setPromos] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // composer state
  const [postType, setPostType] = useState("media");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("general");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("free");
  const [ppvPrice, setPpvPrice] = useState(5);
  const [promoOn, setPromoOn] = useState(false);
  const [promoPrice, setPromoPrice] = useState(3);
  const [promoHours, setPromoHours] = useState(24);
  const [media, setMedia] = useState([]); // {id, kind, preview}
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  const isCreator = user?.role === "creator" || user?.role === "admin";

  const load = useCallback(() => {
    if (!isCreator) return;
    api.get("/dashboard/creator").then(({ data }) => setDash(data)).catch(() => {});
    api.get(`/users/${user.username}/posts`).then(({ data }) => setPosts(data)).catch(() => {});
    api.get("/connect/status").then(({ data }) => setConnect(data)).catch(() => {});
    api.get("/dashboard/promos").then(({ data }) => setPromos(data)).catch(() => {});
  }, [isCreator, user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const c = params.get("connect");
    if (c === "done") { toast.success("Payout details submitted — verifying with Stripe…"); }
    if (c === "refresh") { toast("Let's finish connecting your payouts."); }
    if (c) { params.delete("connect"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      if (data.connect_available && data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error(data.message || "Connect is not available yet.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payout setup");
    } finally { setConnecting(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, { id: data.id, kind: data.kind }]);
      } catch { toast.error(`Upload failed: ${file.name}`); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeMedia = (id) => setMedia((m) => m.filter((x) => x.id !== id));

  const publish = async () => {
    if (!media.length) return toast.error("Add at least one file");
    if (postType === "audio" && !media.some((m) => m.kind === "audio")) return toast.error("Add an audio file");
    const usePromo = visibility === "ppv" && promoOn;
    if (usePromo && Number(promoPrice) >= Number(ppvPrice)) return toast.error("Promo price must be lower than the regular price");
    setPosting(true);
    try {
      await api.post("/posts", {
        title: title || null,
        caption,
        category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        visibility,
        ppv_price: visibility === "ppv" ? Number(ppvPrice) : 0,
        promo_price: usePromo ? Number(promoPrice) : 0,
        promo_hours: usePromo ? Number(promoHours) : 0,
        post_type: postType,
        media,
      });
      toast.success("Published!");
      setTitle(""); setCaption(""); setTags(""); setMedia([]); setVisibility("free"); setPromoOn(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not publish");
    } finally { setPosting(false); }
  };

  if (!isCreator) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="studio-locked">
        <h1 className="font-display text-4xl mb-3">Creator Studio</h1>
        <p className="text-muted-foreground mb-6">You're signed in as a fan. Studio is for creators.</p>
        <button onClick={() => navigate("/settings")} data-testid="become-creator-btn"
          className="px-8 py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm">Go to settings</button>
      </div>
    );
  }

  const Stat = ({ icon, label, value, testId }) => (
    <div className="bg-card border border-border p-5" data-testid={testId}>
      <div className="text-primary mb-3">{icon}</div>
      <p className="font-display text-3xl">{value}</p>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="studio-page">
      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="font-display text-5xl md:text-6xl leading-none mb-8">Creator <span className="italic text-primary">studio</span></motion.h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={<CurrencyDollar size={26} weight="fill" />} label="Earnings" value={`$${dash?.earnings_total ?? 0}`} testId="stat-earnings" />
        <Stat icon={<Users size={26} weight="fill" />} label="Subscribers" value={dash?.stats?.subscribers ?? 0} testId="stat-subs" />
        <Stat icon={<Users size={26} weight="fill" />} label="Followers" value={dash?.stats?.followers ?? 0} testId="stat-followers" />
        <Stat icon={<ImageSquare size={26} weight="fill" />} label="Posts" value={dash?.stats?.posts ?? 0} testId="stat-posts" />
      </div>

      {/* Payouts / Stripe Connect */}
      <div data-testid="payouts-card" className="bg-card border border-border p-6 mb-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="h-12 w-12 shrink-0 bg-primary/15 border border-primary/40 flex items-center justify-center">
          <Bank size={24} weight="fill" className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-accent font-bold text-lg flex items-center gap-2">
            Payouts
            {connect?.payouts_enabled && <span data-testid="payouts-active" className="inline-flex items-center gap-1 text-primary text-xs uppercase tracking-widest"><CheckCircle size={16} weight="fill" /> Active</span>}
          </h3>
          <p className="text-sm text-muted-foreground">
            {connect?.payouts_enabled
              ? `You're getting paid directly. You keep ${100 - (connect?.platform_fee_pct ?? 20)}% of every sale.`
              : connect?.connected
              ? "Almost there — finish your Stripe verification to start receiving payouts."
              : `Connect Stripe to get paid directly. You keep ${100 - (connect?.platform_fee_pct ?? 20)}% of every subscription, unlock and tip.`}
          </p>
        </div>
        {!connect?.payouts_enabled && (
          <button data-testid="connect-payouts-btn" onClick={startConnect} disabled={connecting}
            className="px-6 py-3 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-xs transition-transform active:scale-95 hover:bg-[#e6004c] disabled:opacity-60 shrink-0">
            {connecting ? "Opening…" : connect?.connected ? "Finish setup" : "Connect payouts"}
          </button>
        )}
      </div>

      {/* Promo / PPV analytics */}
      {promos?.posts?.length > 0 && (
        <div data-testid="promo-analytics" className="bg-card border border-border p-6 mb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-3xl flex items-center gap-2"><ChartLineUp size={26} weight="fill" className="text-primary" /> Offer performance</h2>
            <div className="text-right">
              <p className="font-display text-2xl text-primary" data-testid="promo-total-revenue">${promos.total_revenue}</p>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{promos.total_unlocks} unlocks</p>
            </div>
          </div>
          <div className="flex flex-col divide-y divide-border">
            {promos.posts.map((p) => (
              <div key={p.post_id} data-testid={`promo-row-${p.post_id}`} className="py-3 flex items-center gap-3">
                <div className="h-9 w-9 shrink-0 bg-background border border-border flex items-center justify-center">
                  <LockKey size={16} className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{p.title}</p>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    {p.has_promo ? (
                      <>
                        <span className="line-through">${p.ppv_price}</span>
                        <span className="text-primary font-bold">${p.promo_price} offer</span>
                        {p.promo_active
                          ? <span className="inline-flex items-center gap-1 text-primary"><Timer size={12} weight="fill" /><Countdown to={p.promo_expires_at} testId={`promo-countdown-${p.post_id}`} /></span>
                          : <span className="uppercase tracking-widest">Ended</span>}
                      </>
                    ) : <span>${p.ppv_price} PPV · {p.media_count} photo{p.media_count > 1 ? "s" : ""}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-accent font-bold text-lg tabular-nums" data-testid={`promo-unlocks-${p.post_id}`}>{p.unlocks}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">unlocks · ${p.revenue}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* composer */}
      <div className="bg-card border border-border p-6 mb-12" data-testid="composer">
        <h2 className="font-display text-3xl mb-6">New drop</h2>

        <div className="flex gap-2 mb-5">
          <button data-testid="type-media" onClick={() => setPostType("media")}
            className={`flex items-center gap-2 px-4 py-2.5 border font-accent text-xs uppercase tracking-widest transition-colors ${postType === "media" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
            <ImageIcon size={16} /> Photo / Video
          </button>
          <button data-testid="type-audio" onClick={() => setPostType("audio")}
            className={`flex items-center gap-2 px-4 py-2.5 border font-accent text-xs uppercase tracking-widest transition-colors ${postType === "audio" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
            <MusicNotes size={16} /> Audio story
          </button>
        </div>

        {(postType === "audio" || title) && (
          <input data-testid="composer-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. 'Midnight Whisper')"
            className="w-full bg-background border border-border px-4 py-3 mb-4 font-display text-xl focus:border-primary outline-none" />
        )}

        <textarea data-testid="composer-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Write a caption…" rows={3}
          className="w-full bg-background border border-border px-4 py-3 mb-4 focus:border-primary outline-none resize-none" />

        {/* uploads */}
        <div className="mb-4">
          <label data-testid="upload-label" className="flex items-center justify-center gap-2 border border-dashed border-border py-6 cursor-pointer hover:border-primary transition-colors">
            <UploadSimple size={20} className="text-primary" />
            <span className="font-accent text-sm uppercase tracking-widest text-muted-foreground">
              {uploading ? "Uploading…" : postType === "audio" ? "Upload audio (+ cover image)" : "Upload photos / videos"}
            </span>
            <input data-testid="file-input" type="file" multiple hidden onChange={handleUpload}
              accept={postType === "audio" ? "audio/*,image/*" : "image/*,video/*"} />
          </label>
          {media.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {media.map((m) => (
                <div key={m.id} className="relative h-20 w-20 bg-background border border-border flex items-center justify-center overflow-hidden">
                  {m.kind === "image" ? <img src={fileUrl(m.id)} alt="" className="h-full w-full object-cover" />
                    : m.kind === "audio" ? <MusicNotes size={24} className="text-primary" />
                    : <span className="text-[10px] text-muted-foreground">VIDEO</span>}
                  <button data-testid={`remove-media-${m.id}`} onClick={() => removeMedia(m.id)} className="absolute top-0 right-0 bg-black/70 p-0.5"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="composer-category" className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                {CATS.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Access</label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="composer-visibility" className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="free">Free (everyone)</SelectItem>
                <SelectItem value="subscribers">Subscribers only</SelectItem>
                <SelectItem value="ppv">Pay-per-view</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {visibility === "ppv" && (
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">PPV price ($)</label>
              <input data-testid="composer-ppv" type="number" min="1" step="1" value={ppvPrice} onChange={(e) => setPpvPrice(e.target.value)}
                className="w-full bg-background border border-border px-4 py-2.5 focus:border-primary outline-none" />
            </div>
          )}
        </div>

        {/* limited-time promo */}
        {visibility === "ppv" && (
          <div className="mb-4 border border-border p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="composer-promo-toggle" type="checkbox" checked={promoOn} onChange={(e) => setPromoOn(e.target.checked)}
                className="h-4 w-4 accent-primary" />
              <span className="font-accent text-sm uppercase tracking-widest">Limited-time launch offer</span>
              <span className="text-xs text-muted-foreground">— adds a live countdown to nudge buyers</span>
            </label>
            {promoOn && (
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Offer price ($)</label>
                  <input data-testid="composer-promo-price" type="number" min="1" step="1" value={promoPrice} onChange={(e) => setPromoPrice(e.target.value)}
                    className="w-full bg-background border border-border px-4 py-2.5 focus:border-primary outline-none" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Ends in</label>
                  <Select value={String(promoHours)} onValueChange={(v) => setPromoHours(Number(v))}>
                    <SelectTrigger data-testid="composer-promo-hours" className="bg-background border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-border">
                      {[["6", "6 hours"], ["12", "12 hours"], ["24", "24 hours"], ["48", "48 hours"], ["72", "72 hours"]].map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}

        <input data-testid="composer-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma separated (e.g. soles, arch, tease)"
          className="w-full bg-background border border-border px-4 py-3 mb-5 focus:border-primary outline-none" />

        <button data-testid="publish-btn" onClick={publish} disabled={posting}
          className="px-8 py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95 hover:bg-[#e6004c] disabled:opacity-60">
          {posting ? "Publishing…" : "Publish drop"}
        </button>
      </div>

      {/* recent posts */}
      <h2 className="font-display text-3xl mb-4">Your posts</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {posts.map((p) => (
          <div key={p.id} onClick={() => navigate(`/post/${p.id}`)} data-testid={`studio-post-${p.id}`}
            className="aspect-square bg-card border border-border overflow-hidden cursor-pointer relative group">
            {p.post_type === "audio" ? (
              <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-secondary/30 to-primary/20"><MusicNotes size={30} className="text-primary" /></div>
            ) : p.media?.[0] ? (
              <img src={fileUrl(p.media[0].id)} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : <div className="h-full w-full bg-muted" />}
            <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 glass border border-white/20 uppercase">{p.visibility}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
